/**
 * GATE ON A PERMISSION, NEVER ON A GROUP NAME.
 *
 * The frontend's checks are for usability only: every act is authorised again
 * on the server. The codes are the backend's permission enum (03-10-2026):
 * the constant name is the wire string, the description is the matrix label,
 * and `module` is the top-menu band. Supplied branch permissions are
 * authoritative. `buildSession` flattens `modules`. Missing or empty grants
 * offer no protected screens. An unknown string grants no known action; role
 * names never imply access.
 *
 * This replaces the former dotted ids (`admin.staff`, `dispense.view`, …).
 * A session that still carries one of those opens nothing they used to open.
 */
import type { Session } from "./session";

/** Known backend codes, grouped by top-menu module. The live matrix follows the server catalogue order. */
export const PERMISSION_MODULES = [
  { module: "DASHBOARD", permissions: ["DASHBOARD"] },
  { module: "PATIENTS", permissions: ["VIEW_PATIENT", "EDIT_PATIENT"] },
  {
    module: "DISPENSING",
    permissions: [
      "VIEW_DISPENSING",
      "VET_PRESCRIPTIONS",
      "PICK_ASSEMBLE",
      "ACCURACY_CHECK",
      "ENDORSE_SUPPLY",
      "OVERRIDE_PHASE_LOCK",
      "ISSUE_MEDICATION",
    ],
  },
  { module: "POS", permissions: ["OPERATE_POS", "REFUND", "COUNT_DRAWER", "REOPEN_CASHIER"] },
  { module: "INVENTORY", permissions: ["VIEW_INVENTORY", "ADJUST_STOCK", "RECEIVE_STOCK"] },
  { module: "REPORTS", permissions: ["VIEW_REPORT"] },
  {
    module: "SETTINGS",
    permissions: ["EDIT_STAFF", "EDIT_ACCESS_RIGHTS", "VIEW_COMPANY", "EDIT_COMPANY"],
  },
] as const;

export const PERMISSIONS = PERMISSION_MODULES.flatMap((band) => band.permissions);

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Acts that need the PERSON to be a declared registered pharmacist, whatever
 * their group grants. Being registered is a fact about a human being; a group
 * is a job title a pharmacy chose. `OVERRIDE_PHASE_LOCK` is that act.
 * `register.sign` is not in the enum, so it is not gated here.
 */
export const NEEDS_REGISTRATION: ReadonlySet<Permission> = new Set(["OVERRIDE_PHASE_LOCK"]);

export function meetsRegistration(permission: Permission, registeredPharmacist: boolean): boolean {
  return !NEEDS_REGISTRATION.has(permission) || registeredPharmacist;
}

/** Signed in, not expired, granted, and registered where the act needs it. */
export function can(
  session: Session | null | undefined,
  permission: Permission,
  now: Date = new Date(),
): boolean {
  if (!session) return false;
  // Absent is not zero: no expiry stated means the server did not say, and the
  // server ends the session by answering 401 — not this clock.
  if (session.expiresAt !== null && new Date(session.expiresAt).getTime() <= now.getTime()) {
    return false;
  }
  if (!session.permissionsSupplied) return false;
  if (!session.permissions.includes(permission)) return false;
  return meetsRegistration(permission, session.registeredPharmacist);
}
